import { apiRequest } from '@/lib/api';

// Agent types
export interface AgentTriggerConfig {
  cronExpression?: string;
}

export interface AgentConditionConfig {
  sql: string;
  threshold?: number;
}

export interface AgentEmailConfig {
  to: string[];
  subject: string;
  bodyTemplate?: string;
}


export interface AgentWebhookConfig {
  url: string;
  method: 'POST' | 'GET';
  headers?: Record<string, string>;
}

export interface AgentActionConfig {
  email?: AgentEmailConfig;
  webhook?: AgentWebhookConfig;
}

export interface Agent {
  id: string;
  project_id: string;
  build_id: number;
  user_id: number | null;
  name: string;
  description: string | null;
  enabled: boolean;
  trigger_type: 'cron' | 'webhook' | 'manual';
  trigger_config: AgentTriggerConfig;
  condition_type: 'sql_changed' | 'sql_threshold' | 'always';
  condition_config: AgentConditionConfig;
  action_type: 'email' | 'webhook';
  action_config: AgentActionConfig;
  last_result_hash: string | null;
  last_run_at: string | null;
  last_run_status: 'success' | 'failed' | 'skipped' | null;
  created_at: string;
  updated_at: string;
}

export interface AgentExecution {
  id: string;
  agent_id: string;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  trigger_reason: 'scheduled' | 'manual' | 'webhook' | 'dry_run';
  status: 'running' | 'success' | 'failed' | 'skipped';
  condition_result: {
    shouldAct: boolean;
    rowCount: number;
    changedRows?: number;
    resultHash?: string;
    // Query data for debugging
    columns?: string[];
    rows?: unknown[][];
    sql?: string;
  } | null;
  action_result: {
    success: boolean;
    message: string;
    skipped?: boolean;
  } | null;
  error: string | null;
}

export interface CreateAgentInput {
  name: string;
  description?: string;
  buildId: number;
  userId?: number;
  trigger: {
    type: 'cron' | 'webhook' | 'manual';
    cronExpression?: string;
  };
  condition: {
    type: 'sql_changed' | 'sql_threshold' | 'always';
    sql: string;
    threshold?: number;
  };
  action: {
    type: 'email' | 'webhook';
    email?: AgentEmailConfig;
    webhook?: AgentWebhookConfig;
  };
}

export interface AgentsListResponse {
  agents: Agent[];
  total: number;
  limit: number;
  offset: number;
}

export interface ExecutionsListResponse {
  executions: AgentExecution[];
}

// API Functions

export async function getAgents(
  projectId: string,
  limit: number = 50,
  offset: number = 0
): Promise<AgentsListResponse> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/agents?limit=${limit}&offset=${offset}`);
}

export async function createAgent(
  projectId: string,
  input: CreateAgentInput
): Promise<Agent> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/agents`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getAgent(agentId: string): Promise<Agent> {
  return apiRequest(`/api/ai/agent/agents/${agentId}`);
}

export async function updateAgent(
  agentId: string,
  updates: Partial<{
    name: string;
    description: string | null;
    enabled: boolean;
    trigger_config: AgentTriggerConfig;
    condition_config: AgentConditionConfig;
    action_config: AgentActionConfig;
  }>
): Promise<Agent> {
  return apiRequest(`/api/ai/agent/agents/${agentId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteAgent(agentId: string): Promise<{ success: boolean }> {
  return apiRequest(`/api/ai/agent/agents/${agentId}`, {
    method: 'DELETE',
  });
}

export async function triggerAgent(agentId: string): Promise<AgentExecution> {
  return apiRequest(`/api/ai/agent/agents/${agentId}/trigger`, {
    method: 'POST',
  });
}

export async function dryRunAgent(agentId: string): Promise<AgentExecution> {
  return apiRequest(`/api/ai/agent/agents/${agentId}/dry-run`, {
    method: 'POST',
  });
}

export interface TestNotificationResult {
  success: boolean;
  message: string;
  testData: {
    columns: string[];
    rows: unknown[][];
    rowCount: number;
  };
}

export async function testAgentNotification(agentId: string): Promise<TestNotificationResult> {
  return apiRequest(`/api/ai/agent/agents/${agentId}/test-notification`, {
    method: 'POST',
  });
}

export async function getAgentExecutions(
  agentId: string,
  limit: number = 50
): Promise<ExecutionsListResponse> {
  return apiRequest(`/api/ai/agent/agents/${agentId}/executions?limit=${limit}`);
}

// WebSocket token for authenticated connections
export interface WsTokenResponse {
  token: string;
  expiresIn: number;
}

export async function getWsToken(
  projectId: string,
  buildId: number
): Promise<WsTokenResponse> {
  return apiRequest(`/api/ai/ws-token/${projectId}/${buildId}`);
}

// Test query execution
export interface TestQueryResult {
  columns: string[];
  rows: unknown[][];
  rowCount?: number;
  truncated?: boolean;
  error?: string;
}

export async function testQuery(
  projectId: string,
  buildId: number,
  sql: string
): Promise<TestQueryResult> {
  return apiRequest(`/api/ai/test-query/${projectId}/${buildId}`, {
    method: 'POST',
    body: JSON.stringify({ sql }),
  });
}
